/**
 * BuilderPage — generic report builder.
 *
 * Three-rail composer (Rows / Measures / Filters) + result table built on
 * shadcn primitives. Templates come from the Firestore-backed `reports`
 * collection (system templates with organizationId=null + org-private reports).
 * Super admins can save any config as a system template visible to every tenant.
 *
 * Rows are clickable — click drills into the matching executions list, which
 * in turn links to RunDetail (AT log, pcap, step results).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, Wrench, FileText, Save, Trash2, Pencil, ChevronRight, Download, CalendarClock, CornerUpLeft } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Badge, Spinner, EmptyState, DeleteConfirmDialog } from '@/components/ui';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import {
  fetchCatalog, runQuery, listBuilderRuns, exportReport,
  listSavedReports, createSavedReport, updateSavedReport, deleteSavedReport,
} from '@/services/reportBuilderService';
import { aggLabel, formatMeasureValue, formatDimValue } from './reportFormat';
import { useDateFormat } from '@/hooks/useDateFormat';
import ScheduleDeliveryModal from '@/components/reports/ScheduleDeliveryModal';
import BuilderRunsTable, { resolveRunColumnsClient, MEASURES_WITH_FIXED_COLUMN } from '@/components/reports/BuilderRunsTable';
import type {
  Aggregation,
  BuilderConfig,
  BuilderFilter,
  BuilderMeasure,
  BuilderQueryResponse,
  BuilderResultRow,
  BuilderRunsQueryResponse,
  BuilderRunRow,
  DimensionDef,
  MeasureDef,
  ReportBuilderCatalog,
  RunFieldDef,
  SavedReport,
  SavedReportCategory,
} from '@simcheck/shared-types';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const DATE_PRESETS: { value: BuilderConfig['dateRange']; label: string }[] = [
  { value: 'last_24_hours', label: 'Last 24 hours' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
];

const CATEGORIES: { value: SavedReportCategory; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'data', label: 'Data' },
  { value: 'coverage', label: 'Coverage' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'failures', label: 'Failures' },
  { value: 'voice', label: 'Voice' },
  { value: 'custom', label: 'Custom' },
];

const DEFAULT_CONFIG: BuilderConfig = {
  rows: ['country_code'],
  columns: [],
  measures: [{ measureId: 'execution_count', aggregation: 'count' }],
  filters: [],
  dateRange: 'last_7_days',
};

// Connectivity source (#252): hourly/daily KPIs of persistent-attach / PLMN-hunt
// modems. No run behind a row, so it always needs a grouping dimension.
const DEFAULT_CONNECTIVITY_CONFIG: BuilderConfig = {
  source: 'connectivity',
  rows: ['conn_modem'],
  columns: [],
  measures: [
    { measureId: 'conn_uptime_pct', aggregation: 'avg' },
    { measureId: 'conn_attempts', aggregation: 'sum' },
    { measureId: 'conn_attach_success_rate', aggregation: 'avg' },
  ],
  filters: [],
  dateRange: 'last_7_days',
};

/** Which catalog entries a source may use. */
function dimAllowedForSource(d: DimensionDef, source: 'executions' | 'connectivity'): boolean {
  const ds = (d as any).source ?? 'executions';
  return ds === 'any' || ds === source;
}
function measureAllowedForSource(m: MeasureDef, source: 'executions' | 'connectivity'): boolean {
  return ((m as any).source ?? 'executions') === source;
}

function decodeParam<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw)) as T;
  } catch {
    return null;
  }
}

/** Default run-list column ids (mirrors DEFAULT_RUN_COLUMNS in the backend catalog). */
const DEFAULT_RUN_COLUMN_IDS = [
  'run_started', 'run_flow', 'run_status', 'run_duration',
  'run_operator', 'run_location', 'run_esim_profile', 'run_modem', 'run_rat', 'run_failure_class',
];

/** Compact, removable pill used across the composer's chip lists. */
function Chip({ children, onRemove }: { children: React.ReactNode; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 py-1 pl-2.5 pr-1 text-sm">
      {children}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

export default function BuilderPage() {
  const { activeOrganization, isSuperAdmin, userRole } = useAuth();
  const canManageDeliveries = isSuperAdmin || userRole === 'owner' || userRole === 'admin';
  const toast = useToast();
  const navigate = useNavigate();
  const { timeZone } = useDateFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetId = searchParams.get('preset');

  const [catalog, setCatalog] = useState<ReportBuilderCatalog | null>(null);
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [config, setConfig] = useState<BuilderConfig>(DEFAULT_CONFIG);
  const [loadedReportId, setLoadedReportId] = useState<string | null>(null);
  const [result, setResult] = useState<BuilderQueryResponse | null>(null);
  const [runsResult, setRunsResult] = useState<BuilderRunsQueryResponse | null>(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Saved report delete waiting for confirmation in the DeleteConfirmDialog
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Catalog (frozen, org-independent)
  useEffect(() => {
    let cancelled = false;
    fetchCatalog()
      .then((c) => { if (!cancelled) setCatalog(c); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load catalog'); })
      .finally(() => { if (!cancelled) setLoadingCatalog(false); });
    return () => { cancelled = true; };
  }, []);

  // Saved reports (system + org)
  const loadSaved = React.useCallback(async () => {
    if (!activeOrganization?.id) return;
    setLoadingReports(true);
    try {
      const reports = await listSavedReports(activeOrganization.id);
      setSavedReports(reports);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load reports');
    } finally {
      setLoadingReports(false);
    }
  }, [activeOrganization?.id]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // When preset id changes, hydrate config from it
  useEffect(() => {
    if (!presetId) {
      setLoadedReportId(null);
      return;
    }
    const found = savedReports.find((r) => r.id === presetId);
    if (found) {
      setConfig(found.config);
      setLoadedReportId(found.id);
    }
  }, [presetId, savedReports]);

  const dimById = useMemo(() => {
    const map = new Map<string, DimensionDef>();
    catalog?.dimensions.forEach((d) => map.set(d.id, d));
    return map;
  }, [catalog]);
  const measureById = useMemo(() => {
    const map = new Map<string, MeasureDef>();
    catalog?.measures.forEach((m) => map.set(m.id, m));
    return map;
  }, [catalog]);
  const runFieldById = useMemo(() => {
    const map = new Map<string, RunFieldDef>();
    catalog?.runFields?.forEach((f) => map.set(f.id, f));
    return map;
  }, [catalog]);

  // Report SHAPE is derived from grouping. Drilling from an aggregate cell
  // converts that row's dim values into `eq` filters and clears grouping →
  // the same page renders the underlying runs. State rides the `drill` URL
  // param so browser Back/Forward restore the summary.
  const drillDims = useMemo(
    () => decodeParam<Record<string, string | number | boolean | null>>(searchParams.get('drill')),
    [searchParams],
  );
  const effectiveConfig = useMemo<BuilderConfig>(() => {
    if (!drillDims) return config;
    const drillFilters: BuilderFilter[] = Object.entries(drillDims).map(([dimensionId, v]) => ({
      dimensionId, op: 'eq', values: [v ?? null],
    }));
    return { ...config, rows: [], filters: [...config.filters, ...drillFilters] };
  }, [config, drillDims]);
  const isRunList = effectiveConfig.rows.length === 0;

  // Consume a `cfg` param (from an old /reports/builder/runs redirect) once into
  // state, then strip it so later user edits aren't clobbered on refresh.
  const consumedCfg = useRef(false);
  useEffect(() => {
    if (consumedCfg.current) return;
    consumedCfg.current = true;
    const raw = searchParams.get('cfg');
    if (!raw) return;
    const decoded = decodeParam<BuilderConfig>(raw);
    const next = new URLSearchParams(searchParams);
    next.delete('cfg');
    if (decoded) setConfig(decoded);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-run on first load + whenever the effective config changes. The shape
  // is derived from grouping: no row dims ⇒ run-list (listBuilderRuns);
  // otherwise the aggregate query (runQuery).
  useEffect(() => {
    if (!activeOrganization?.id || !catalog) return;
    let cancelled = false;
    setRunning(true);
    setError(null);
    const run = isRunList
      ? listBuilderRuns(activeOrganization.id, effectiveConfig, {}, timeZone).then((r) => {
          if (!cancelled) { setRunsResult(r); setResult(null); }
        })
      : runQuery(activeOrganization.id, effectiveConfig, timeZone).then((r) => {
          if (!cancelled) { setResult(r); setRunsResult(null); }
        });
    run
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Query failed'); })
      .finally(() => { if (!cancelled) setRunning(false); });
    return () => { cancelled = true; };
  }, [activeOrganization?.id, catalog, effectiveConfig, isRunList, timeZone]);

  // Mutators
  const addRowDim = (dimId: string) => setConfig((c) => ({ ...c, rows: [...c.rows, dimId] }));
  const removeRowDim = (dimId: string) => setConfig((c) => ({ ...c, rows: c.rows.filter((r) => r !== dimId) }));

  const addMeasure = (measureId: string) => {
    const def = measureById.get(measureId);
    if (!def) return;
    setConfig((c) => ({ ...c, measures: [...c.measures, { measureId, aggregation: def.defaultAggregation }] }));
  };
  const removeMeasure = (index: number) =>
    setConfig((c) => ({ ...c, measures: c.measures.filter((_, i) => i !== index) }));
  const updateMeasureAgg = (index: number, agg: Aggregation) =>
    setConfig((c) => ({ ...c, measures: c.measures.map((m, i) => (i === index ? { ...m, aggregation: agg } : m)) }));

  const addFilter = (dimId: string, value: string) => {
    setConfig((c) => ({
      ...c,
      filters: [...c.filters.filter((f) => f.dimensionId !== dimId), { dimensionId: dimId, op: 'eq', values: [value] }],
    }));
  };
  const removeFilter = (idx: number) =>
    setConfig((c) => ({ ...c, filters: c.filters.filter((_, i) => i !== idx) }));

  // Run-list columns (used when there is no grouping). Undefined `runColumns`
  // renders the default set; the first edit materializes an explicit list.
  const runColumnIds = config.runColumns ?? DEFAULT_RUN_COLUMN_IDS;
  const addRunColumn = (id: string) => setConfig((c) => ({ ...c, runColumns: [...(c.runColumns ?? DEFAULT_RUN_COLUMN_IDS), id] }));
  const removeRunColumn = (id: string) =>
    setConfig((c) => ({ ...c, runColumns: (c.runColumns ?? DEFAULT_RUN_COLUMN_IDS).filter((x) => x !== id) }));

  // Flow-parameter columns — raw `execution.outputs` keys (flow inputs + Variable
  // values, incl. a random_choice pick). Suggestions come from the scanned runs
  // (`runsResult.meta.paramKeys`); any typed key also works.
  const paramColumns = config.paramColumns ?? [];
  const addParamColumn = (key: string) => {
    const k = key.trim();
    if (!k) return;
    setConfig((c) => (c.paramColumns ?? []).includes(k) ? c : { ...c, paramColumns: [...(c.paramColumns ?? []), k] });
  };
  const removeParamColumn = (key: string) =>
    setConfig((c) => ({ ...c, paramColumns: (c.paramColumns ?? []).filter((x) => x !== key) }));

  // Aggregate row click → drill: pin the row's dim values as filters (URL param).
  const handleRowClick = (row: BuilderResultRow) => {
    const next = new URLSearchParams(searchParams);
    next.set('drill', encodeURIComponent(JSON.stringify(row.dims)));
    setSearchParams(next);
  };
  const exitDrill = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('drill');
    setSearchParams(next);
  };
  const drillSummary = drillDims
    ? Object.entries(drillDims)
        .map(([k, v]) => `${dimById.get(k)?.displayName ?? k} = ${v === null || v === undefined || v === '' ? '—' : String(v)}`)
        .join(' · ')
    : '';

  const loadedReport = savedReports.find((r) => r.id === loadedReportId) ?? null;
  const isLoadedReportEditable = !!loadedReport && (
    isSuperAdmin || loadedReport.organizationId === activeOrganization?.id
  );

  // When drilled, persist the drilled (run-list) config — "drill → Save" makes a
  // scoped CDR report. Otherwise persist the config as composed.
  const configToPersist = drillDims ? effectiveConfig : config;

  const handleSave = async (input: { name: string; description: string; category: SavedReportCategory; asSystemTemplate: boolean }) => {
    if (!activeOrganization?.id) return;
    try {
      const saved = await createSavedReport(activeOrganization.id, {
        name: input.name,
        description: input.description || undefined,
        category: input.category,
        config: configToPersist,
        asSystemTemplate: input.asSystemTemplate && isSuperAdmin,
      });
      toast.success(`Report "${saved.name}" saved`);
      await loadSaved();
      // Switch the URL to the newly-saved report so subsequent edits target it
      const next = new URLSearchParams(searchParams);
      next.set('preset', saved.id);
      setSearchParams(next);
      setSaveOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const handleEdit = async (input: { name: string; description: string; category: SavedReportCategory; asSystemTemplate: boolean }) => {
    if (!activeOrganization?.id || !loadedReport) return;
    try {
      const patch: any = {
        name: input.name,
        description: input.description || undefined,
        category: input.category,
        config: configToPersist,
      };
      if (isSuperAdmin) patch.asSystemTemplate = input.asSystemTemplate;
      const updated = await updateSavedReport(activeOrganization.id, loadedReport.id, patch);
      toast.success(`Report "${updated.name}" updated`);
      await loadSaved();
      setEditOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    if (!activeOrganization?.id) return;
    setExporting(true);
    try {
      const baseName = loadedReport?.name?.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase() || 'report';
      const configWithName: BuilderConfig = loadedReport?.name
        ? { ...effectiveConfig, name: loadedReport.name }
        : effectiveConfig;
      await exportReport(
        activeOrganization.id,
        configWithName,
        format,
        `${baseName}-${Date.now()}.${format}`,
        timeZone,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!activeOrganization?.id || !loadedReport) return;
    try {
      await deleteSavedReport(activeOrganization.id, loadedReport.id);
      toast.success(`Report "${loadedReport.name}" deleted`);
      await loadSaved();
      const next = new URLSearchParams(searchParams);
      next.delete('preset');
      setSearchParams(next);
      setConfig(DEFAULT_CONFIG);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  // Render-time derived values for the run-list view.
  const hasResults = isRunList
    ? !!runsResult && runsResult.runs.length > 0
    : !!result && result.rows.length > 0;
  const resolvedRunColumns = useMemo(
    () => resolveRunColumnsClient(effectiveConfig, runsResult?.runs ?? [], runFieldById),
    [effectiveConfig, runsResult, runFieldById],
  );
  const runListMeasureDefs = useMemo(() => {
    const seen = new Set<string>();
    const out: MeasureDef[] = [];
    for (const m of effectiveConfig.measures) {
      if (MEASURES_WITH_FIXED_COLUMN.has(m.measureId) || seen.has(m.measureId)) continue;
      const def = measureById.get(m.measureId);
      if (!def) continue;
      seen.add(m.measureId);
      out.push(def);
    }
    return out;
  }, [effectiveConfig, measureById]);

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              {loadedReport ? loadedReport.name : 'Report builder'}
            </h1>
            <p className="text-body text-muted-foreground">
              {loadedReport?.description ?? 'Pick group-by rows, measures, and filters — results update live. With no grouping you get one row per run; click an aggregated row to drill into its runs.'}
            </p>
            {loadedReport?.organizationId === null ? (
              <Badge variant="secondary" className="mt-2">System template</Badge>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <TemplatePicker
              reports={savedReports}
              loading={loadingReports}
              activeId={loadedReportId}
              activeOrgId={activeOrganization?.id ?? null}
              onPick={(report) => {
                const next = new URLSearchParams(searchParams);
                next.set('preset', report.id);
                setSearchParams(next);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  icon={exporting ? <Spinner className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                  disabled={exporting || !hasResults}
                >
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')}>CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" icon={<Save className="w-4 h-4" />} onClick={() => setSaveOpen(true)}>
              Save as
            </Button>
            {loadedReport && canManageDeliveries ? (
              <Button variant="outline" size="sm" icon={<CalendarClock className="w-4 h-4" />} onClick={() => setScheduleOpen(true)}>
                Schedule delivery
              </Button>
            ) : null}
            {loadedReport && isLoadedReportEditable ? (
              <>
                <Button variant="outline" size="sm" icon={<Pencil className="w-4 h-4" />} onClick={() => setEditOpen(true)}>
                  Edit
                </Button>
                <Button variant="outline" size="sm" icon={<Trash2 className="w-4 h-4" />} onClick={() => setConfirmDeleteOpen(true)}>
                  Delete
                </Button>
              </>
            ) : null}
            <Select
              value={config.source ?? 'executions'}
              onValueChange={(v) => {
                // Switching source resets the grouping/measures — the two
                // catalogs don't overlap (#252).
                if (v === (config.source ?? 'executions')) return;
                setResult(null); setRunsResult(null);
                setConfig((c) => (v === 'connectivity'
                  ? { ...DEFAULT_CONNECTIVITY_CONFIG, dateRange: c.dateRange }
                  : { ...DEFAULT_CONFIG, dateRange: c.dateRange }));
              }}
            >
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="executions">Test runs</SelectItem>
                <SelectItem value="connectivity">Connectivity</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={typeof config.dateRange === 'string' ? config.dateRange : 'custom'}
              onValueChange={(v) => setConfig((c) => ({ ...c, dateRange: v as BuilderConfig['dateRange'] }))}
            >
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((p) => (
                  <SelectItem key={String(p.value)} value={String(p.value)}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loadingCatalog && !catalog ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : catalog ? (
          <>
            {/* Composer */}
            <Card padding="lg">
              <div className="space-y-5">
                {/* Top row: Group by · Metrics · Filters — compact wrapping chips */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-3">
                  {/* ROWS (group by) */}
                  <div>
                    <div className="text-caption text-muted-foreground uppercase mb-2">Rows (group by)</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {config.rows.map((dimId) => {
                        const def = dimById.get(dimId);
                        return (
                          <Chip key={dimId} onRemove={() => removeRowDim(dimId)}>
                            <span className="font-medium">{def?.displayName ?? dimId}</span>
                          </Chip>
                        );
                      })}
                      <CatalogPicker
                        catalog={catalog}
                        filterFn={(d) => !config.rows.includes(d.id) && dimAllowedForSource(d, config.source ?? 'executions')}
                        onPick={(dimId) => addRowDim(dimId)}
                        addLabel="Add row dimension"
                      />
                    </div>
                    {config.rows.length === 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        No grouping — one row per run. Add a dimension to summarize.
                      </p>
                    ) : null}
                  </div>

                  {/* MEASURES / METRICS */}
                  <div>
                    <div className="text-caption text-muted-foreground uppercase mb-2">
                      {isRunList ? 'Metrics (per-run column + total)' : 'Measures'}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {config.measures.map((m, i) => {
                        const def = measureById.get(m.measureId);
                        return (
                          <Chip key={i} onRemove={() => removeMeasure(i)}>
                            <span className="font-medium">{def?.displayName ?? m.measureId}</span>
                            {isRunList ? null : (
                              <Select value={m.aggregation} onValueChange={(v) => updateMeasureAgg(i, v as Aggregation)}>
                                <SelectTrigger className="h-6 w-24 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {def?.supportedAggregations.map((a) => (
                                    <SelectItem key={a} value={a}>{aggLabel(a)}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </Chip>
                        );
                      })}
                      <MeasurePicker catalog={catalog} source={config.source ?? 'executions'} onPick={(id) => addMeasure(id)} />
                    </div>
                  </div>

                  {/* FILTERS */}
                  <div>
                    <div className="text-caption text-muted-foreground uppercase mb-2">Filters</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {config.filters.map((f, i) => {
                        const def = dimById.get(f.dimensionId);
                        return (
                          <Chip key={i} onRemove={() => removeFilter(i)}>
                            <span className="text-muted-foreground">{def?.displayName ?? f.dimensionId}</span>
                            <span className="text-muted-foreground">=</span>
                            <span className="font-medium">{String(f.values[0])}</span>
                          </Chip>
                        );
                      })}
                      <FilterPicker catalog={catalog} source={config.source ?? 'executions'} onPick={addFilter} />
                    </div>
                  </div>
                </div>

                {/* RUN COLUMNS — full-width strip, run-list shape only (no grouping). */}
                {config.rows.length === 0 ? (
                  <div className="border-t border-border pt-4">
                    <div className="text-caption text-muted-foreground uppercase mb-2">Run columns</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {runColumnIds.map((id) => {
                        const f = runFieldById.get(id);
                        return (
                          <Chip key={id} onRemove={() => removeRunColumn(id)}>
                            <span className="font-medium">{f?.displayName ?? id}</span>
                          </Chip>
                        );
                      })}
                      <RunFieldPicker
                        catalog={catalog}
                        filterFn={(f) => !runColumnIds.includes(f.id)}
                        onPick={addRunColumn}
                      />
                    </div>

                    {/* FLOW PARAMETERS — raw output keys (flow inputs + Variable
                        values, incl. a random_choice pick) as extra columns. */}
                    <div className="text-caption text-muted-foreground uppercase mt-4 mb-2">Flow parameters</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {paramColumns.map((key) => (
                        <Chip key={key} onRemove={() => removeParamColumn(key)}>
                          <span className="font-mono text-xs">{key}</span>
                        </Chip>
                      ))}
                      <ParamColumnPicker
                        available={(runsResult?.meta?.paramKeys ?? []).filter((k) => !paramColumns.includes(k))}
                        onPick={addParamColumn}
                      />
                      {paramColumns.length === 0 ? (
                        <span className="text-xs text-muted-foreground">
                          Add a flow input / variable (e.g. the dialed destination) as a column.
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </Card>

            {/* Drill breadcrumb — shown when viewing one aggregate cell's runs. */}
            {drillDims ? (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
                <span className="text-sm text-muted-foreground">Runs for:</span>
                <span className="text-sm font-medium">{drillSummary || 'all runs'}</span>
                <Button variant="ghost" size="sm" className="ml-auto" icon={<CornerUpLeft className="w-4 h-4" />} onClick={exitDrill}>
                  Back to summary
                </Button>
              </div>
            ) : null}

            {/* Results */}
            {isRunList ? (
              <>
                {runsResult && runsResult.runs.length > 0 ? (
                  <RunsTotalsStrip config={effectiveConfig} totals={runsResult.totals} totalCount={runsResult.totalCount} measureById={measureById} />
                ) : null}
                <Card padding="none">
                  {running && !runsResult ? (
                    <div className="flex items-center justify-center py-12"><Spinner /></div>
                  ) : runsResult && runsResult.runs.length > 0 ? (
                    <>
                      {runsResult.truncated ? (
                        <div className="px-4 pt-3 text-xs text-muted-foreground">
                          Showing the first {runsResult.runs.length.toLocaleString()} of {runsResult.totalCount.toLocaleString()} runs.
                        </div>
                      ) : null}
                      <BuilderRunsTable
                        runs={runsResult.runs}
                        columns={resolvedRunColumns}
                        paramColumns={effectiveConfig.paramColumns ?? []}
                        measureDefs={runListMeasureDefs}
                        onRowClick={(r: BuilderRunRow) => navigate(`/reports/runs/${r.executionId}`)}
                      />
                    </>
                  ) : (
                    <div className="p-6">
                      <EmptyState
                        icon={<Wrench className="h-10 w-10" />}
                        title="No matching runs"
                        description="No executions match the current filters and date range. Widen the date range or relax the filters."
                      />
                    </div>
                  )}
                </Card>
              </>
            ) : (
              <Card padding="none">
                {running && !result ? (
                  <div className="flex items-center justify-center py-12"><Spinner /></div>
                ) : result ? (
                  <ResultsTable
                    result={result}
                    dimById={dimById}
                    measureById={measureById}
                    onRowClick={handleRowClick}
                  />
                ) : (
                  <div className="p-6">
                    <EmptyState
                      icon={<Wrench className="h-10 w-10" />}
                      title="Configure your report"
                      description="Pick at least one row dimension and one measure, then results will appear here."
                    />
                  </div>
                )}
              </Card>
            )}

            {isRunList && runsResult ? (
              <div className="text-xs text-muted-foreground">
                {running ? <Spinner className="inline-flex" /> : null}{' '}
                Scanned {runsResult.meta.scannedDocs.toLocaleString()} executions ({runsResult.meta.computeMs} ms){runsResult.truncated ? ' · showing the first 1,000' : ''} · catalog v{runsResult.meta.catalogVersion}
              </div>
            ) : result ? (
              <div className="text-xs text-muted-foreground">
                {running ? <Spinner className="inline-flex" /> : null}{' '}
                Scanned {result.meta.scannedDocs.toLocaleString()} executions ({result.meta.computeMs} ms){result.meta.truncated ? ' · ⚠ truncated' : ''} · catalog v{result.meta.catalogVersion}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <SaveReportDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onSubmit={handleSave}
        title="Save report"
        submitLabel="Save"
        canSystem={isSuperAdmin}
        initial={{
          name: loadedReport ? `${loadedReport.name} (copy)` : '',
          description: '',
          category: loadedReport?.category ?? 'custom',
          asSystemTemplate: false,
        }}
      />
      {loadedReport && isLoadedReportEditable ? (
        <SaveReportDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSubmit={handleEdit}
          title="Edit report"
          submitLabel="Save changes"
          canSystem={isSuperAdmin}
          initial={{
            name: loadedReport.name,
            description: loadedReport.description ?? '',
            category: loadedReport.category,
            asSystemTemplate: loadedReport.organizationId === null,
          }}
        />
      ) : null}
      {loadedReport && activeOrganization?.id ? (
        <ScheduleDeliveryModal
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          organizationId={activeOrganization.id}
          savedReportId={loadedReport.id}
          savedReportName={loadedReport.name}
          onCreated={() => navigate('/reports/subscriptions')}
        />
      ) : null}

      <DeleteConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        itemName={loadedReport?.name}
        onConfirm={handleDelete}
      />
    </AppShell>
  );
}

// ----------------------------------------------------------------------------
// Composer pickers (unchanged from prior iteration)
// ----------------------------------------------------------------------------

function CatalogPicker({
  catalog, filterFn, onPick, addLabel,
}: {
  catalog: ReportBuilderCatalog;
  filterFn: (d: DimensionDef) => boolean;
  onPick: (dimId: string) => void;
  addLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const grouped = useMemo(() => {
    const map = new Map<string, DimensionDef[]>();
    catalog.dimensions.filter(filterFn).forEach((d) => {
      const arr = map.get(d.category) ?? [];
      arr.push(d);
      map.set(d.category, arr);
    });
    return Array.from(map.entries());
  }, [catalog, filterFn]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<Plus className="w-4 h-4" />}>{addLabel}</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder="Search dimensions..." />
          <CommandList>
            <CommandEmpty>No dimensions found.</CommandEmpty>
            {grouped.map(([cat, dims]) => (
              <CommandGroup key={cat} heading={cat.toUpperCase()}>
                {dims.map((d) => (
                  <CommandItem
                    key={d.id}
                    value={`${d.displayName} ${d.id}`}
                    onSelect={() => { onPick(d.id); setOpen(false); }}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{d.displayName}</span>
                      {d.description ? <span className="text-xs text-muted-foreground">{d.description}</span> : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function MeasurePicker({ catalog, onPick, source = 'executions' }: { catalog: ReportBuilderCatalog; onPick: (id: string) => void; source?: 'executions' | 'connectivity' }) {
  const [open, setOpen] = useState(false);
  const grouped = useMemo(() => {
    const map = new Map<string, MeasureDef[]>();
    catalog.measures.filter((m) => measureAllowedForSource(m, source)).forEach((m) => {
      const arr = map.get(m.category) ?? [];
      arr.push(m);
      map.set(m.category, arr);
    });
    return Array.from(map.entries());
  }, [catalog, source]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<Plus className="w-4 h-4" />}>Add measure</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="start">
        <Command>
          <CommandInput placeholder="Search measures..." />
          <CommandList>
            <CommandEmpty>No measures found.</CommandEmpty>
            {grouped.map(([cat, ms]) => (
              <CommandGroup key={cat} heading={cat.toUpperCase()}>
                {ms.map((m) => (
                  <CommandItem
                    key={m.id}
                    value={`${m.displayName} ${m.id}`}
                    onSelect={() => { onPick(m.id); setOpen(false); }}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{m.displayName}{m.unit ? ` (${m.unit})` : ''}</span>
                      {m.description ? <span className="text-xs text-muted-foreground">{m.description}</span> : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FilterPicker({ catalog, onPick, source = 'executions' }: { catalog: ReportBuilderCatalog; onPick: (dimId: string, value: string) => void; source?: 'executions' | 'connectivity' }) {
  const [open, setOpen] = useState(false);
  const [selectedDim, setSelectedDim] = useState<DimensionDef | null>(null);

  const filterable = useMemo(
    () => catalog.dimensions.filter((d) => (d.dataType === 'enum' || d.dataType === 'string') && dimAllowedForSource(d, source)),
    [catalog, source],
  );

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSelectedDim(null); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<Plus className="w-4 h-4" />}>Add filter</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        {!selectedDim ? (
          <Command>
            <CommandInput placeholder="Filter by..." />
            <CommandList>
              <CommandEmpty>No fields found.</CommandEmpty>
              <CommandGroup heading="DIMENSIONS">
                {filterable.map((d) => (
                  <CommandItem key={d.id} value={`${d.displayName} ${d.id}`} onSelect={() => setSelectedDim(d)}>
                    {d.displayName}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : selectedDim.dataType === 'enum' && selectedDim.enumValues ? (
          <Command>
            <CommandInput placeholder={`${selectedDim.displayName} =`} />
            <CommandList>
              <CommandEmpty>No values.</CommandEmpty>
              <CommandGroup heading={selectedDim.displayName.toUpperCase()}>
                {selectedDim.enumValues.map((ev) => (
                  <CommandItem key={ev.value} value={ev.label} onSelect={() => { onPick(selectedDim.id, ev.value); setOpen(false); setSelectedDim(null); }}>
                    {ev.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-3">
            <div className="text-xs text-muted-foreground mb-2">{selectedDim.displayName} =</div>
            <input
              autoFocus
              type="text"
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              placeholder="value (press Enter)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const v = (e.target as HTMLInputElement).value;
                  if (v) { onPick(selectedDim.id, v); setOpen(false); setSelectedDim(null); }
                }
              }}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function RunFieldPicker({
  catalog, filterFn, onPick,
}: {
  catalog: ReportBuilderCatalog;
  filterFn: (f: RunFieldDef) => boolean;
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const grouped = useMemo(() => {
    const map = new Map<string, RunFieldDef[]>();
    (catalog.runFields ?? []).filter(filterFn).forEach((f) => {
      const arr = map.get(f.category) ?? [];
      arr.push(f);
      map.set(f.category, arr);
    });
    return Array.from(map.entries());
  }, [catalog, filterFn]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<Plus className="w-4 h-4" />}>Add run column</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder="Search run columns..." />
          <CommandList>
            <CommandEmpty>No columns found.</CommandEmpty>
            {grouped.map(([cat, fs]) => (
              <CommandGroup key={cat} heading={cat.toUpperCase()}>
                {fs.map((f) => (
                  <CommandItem key={f.id} value={`${f.displayName} ${f.id}`} onSelect={() => { onPick(f.id); setOpen(false); }}>
                    <div className="flex flex-col">
                      <span className="font-medium">{f.displayName}</span>
                      {f.description ? <span className="text-xs text-muted-foreground">{f.description}</span> : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ParamColumnPicker({
  available, onPick,
}: {
  available: string[];
  onPick: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const showAdd = trimmed.length > 0 && !available.includes(trimmed);
  const pick = (key: string) => { onPick(key); setQuery(''); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<Plus className="w-4 h-4" />}>Add parameter column</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder="Parameter key (e.g. destination)…" value={query} onValueChange={setQuery} />
          <CommandList>
            {available.length === 0 && !showAdd ? (
              <CommandEmpty>Run the report to discover flow parameters, or type a key.</CommandEmpty>
            ) : (
              <CommandEmpty>No matching parameter.</CommandEmpty>
            )}
            {showAdd ? (
              <CommandGroup heading="CUSTOM">
                <CommandItem value={`add ${trimmed}`} onSelect={() => pick(trimmed)}>
                  Add “<span className="font-mono">{trimmed}</span>”
                </CommandItem>
              </CommandGroup>
            ) : null}
            {available.length > 0 ? (
              <CommandGroup heading="DISCOVERED">
                {available.map((key) => (
                  <CommandItem key={key} value={key} onSelect={() => pick(key)}>
                    <span className="font-mono text-sm">{key}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ----------------------------------------------------------------------------
// Run-list totals strip — the aggregate numbers over every matching run.
// ----------------------------------------------------------------------------

function RunsTotalsStrip({
  config, totals, totalCount, measureById,
}: {
  config: BuilderConfig;
  totals: { sampleSize: number; measures: Record<string, number | null> };
  totalCount: number;
  measureById: Map<string, MeasureDef>;
}) {
  const chips: Array<{ label: string; value: string }> = [
    { label: 'Runs', value: totalCount.toLocaleString() },
  ];
  for (const m of config.measures) {
    if (m.measureId === 'execution_count') continue;
    const def = measureById.get(m.measureId);
    if (!def) continue;
    chips.push({
      label: `${def.displayName} (${aggLabel(m.aggregation)})`,
      value: formatMeasureValue(totals.measures[`${m.measureId}__${m.aggregation}`] ?? null, def, m.aggregation),
    });
  }
  return (
    <div className="flex flex-wrap gap-3">
      {chips.map((c, i) => (
        <div key={i} className="rounded-lg border border-border bg-card px-4 py-2.5 min-w-[120px]">
          <div className="text-caption text-muted-foreground uppercase">{c.label}</div>
          <div className="text-lg font-semibold tabular-nums text-foreground">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Results table
// ----------------------------------------------------------------------------

function ResultsTable({
  result, dimById, measureById, onRowClick,
}: {
  result: BuilderQueryResponse;
  dimById: Map<string, DimensionDef>;
  measureById: Map<string, MeasureDef>;
  onRowClick: (row: BuilderResultRow) => void;
}) {
  if (result.rows.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          title="No rows"
          description="No data matches the current configuration. Widen the date range, remove filters, or pick different dimensions."
        />
      </div>
    );
  }

  const rowDims = result.config.rows.map((id) => dimById.get(id)).filter((d): d is DimensionDef => !!d);
  const measures = result.config.measures.map((m) => ({ m, def: measureById.get(m.measureId) }))
    .filter((x): x is { m: BuilderMeasure; def: MeasureDef } => !!x.def);

  const columns = [
    ...rowDims.map((d) => ({
      key: `dim_${d.id}`,
      header: d.displayName,
      sortable: true,
      sortValue: (r: BuilderResultRow) => r.dims[d.id],
      render: (_: unknown, r: BuilderResultRow) => (
        <span className="font-medium">{formatDimValue(r.dims[d.id], d)}</span>
      ),
    })),
    ...measures.map(({ m, def }) => ({
      key: `m_${m.measureId}_${m.aggregation}`,
      header: `${def.displayName} (${aggLabel(m.aggregation)})`,
      sortable: true,
      sortValue: (r: BuilderResultRow) => r.measures[`${m.measureId}__${m.aggregation}`],
      render: (_: unknown, r: BuilderResultRow) => {
        const value = r.measures[`${m.measureId}__${m.aggregation}`];
        return <span className="tabular-nums">{formatMeasureValue(value ?? null, def, m.aggregation)}</span>;
      },
    })),
    {
      key: 'sample',
      header: 'Runs',
      sortable: true,
      sortValue: (r: BuilderResultRow) => r.sampleSize,
      render: (_: unknown, r: BuilderResultRow) => (
        <Badge variant={r.sampleSize < 5 ? 'secondary' : 'outline'} className="text-xs">{r.sampleSize}</Badge>
      ),
    },
    {
      key: '__drill',
      header: '',
      render: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
    },
  ];

  return (
    <ResponsiveTable<BuilderResultRow>
      data={result.rows}
      keyExtractor={(r) => result.config.rows.map((id) => String(r.dims[id])).join('|')}
      columns={columns}
      onRowClick={onRowClick}
      paginated
      defaultPageSize={25}
    />
  );
}

// ----------------------------------------------------------------------------
// Template picker — sources from API, groups System vs My reports
// ----------------------------------------------------------------------------

function TemplatePicker({
  reports, loading, activeId, activeOrgId, onPick,
}: {
  reports: SavedReport[];
  loading: boolean;
  activeId: string | null;
  activeOrgId: string | null;
  onPick: (report: SavedReport) => void;
}) {
  const [open, setOpen] = useState(false);

  const systemReports = reports.filter((r) => r.organizationId === null);
  const myReports = reports.filter((r) => r.organizationId === activeOrgId);

  // Group system reports by category for readability.
  const groupedSystem = useMemo(() => {
    const map = new Map<string, SavedReport[]>();
    systemReports.forEach((r) => {
      const arr = map.get(r.category) ?? [];
      arr.push(r);
      map.set(r.category, arr);
    });
    return Array.from(map.entries());
  }, [systemReports]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<FileText className="w-4 h-4" />}>
          Templates {reports.length ? `(${reports.length})` : ''}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-96" align="end">
        <Command>
          <CommandInput placeholder="Search reports..." />
          <CommandList>
            {loading ? (
              <div className="flex justify-center py-6"><Spinner /></div>
            ) : (
              <>
                <CommandEmpty>No reports found.</CommandEmpty>
                {groupedSystem.length > 0 ? (
                  <>
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">System templates</div>
                    {groupedSystem.map(([cat, rs]) => (
                      <CommandGroup key={`sys-${cat}`} heading={cat.toUpperCase()}>
                        {rs.map((r) => (
                          <CommandItem
                            key={r.id}
                            value={`${r.name} ${r.description ?? ''}`}
                            onSelect={() => { onPick(r); setOpen(false); }}
                          >
                            <div className="flex flex-col flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium truncate">{r.name}</span>
                                {r.id === activeId ? <Badge variant="secondary" className="text-[10px] py-0">active</Badge> : null}
                              </div>
                              {r.description ? <span className="text-xs text-muted-foreground truncate">{r.description}</span> : null}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </>
                ) : null}
                {myReports.length > 0 ? (
                  <>
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">My reports</div>
                    <CommandGroup>
                      {myReports.map((r) => (
                        <CommandItem
                          key={r.id}
                          value={`${r.name} ${r.description ?? ''}`}
                          onSelect={() => { onPick(r); setOpen(false); }}
                        >
                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{r.name}</span>
                              {r.id === activeId ? <Badge variant="secondary" className="text-[10px] py-0">active</Badge> : null}
                            </div>
                            {r.description ? <span className="text-xs text-muted-foreground truncate">{r.description}</span> : null}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                ) : null}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ----------------------------------------------------------------------------
// Save / edit dialog
// ----------------------------------------------------------------------------

function SaveReportDialog({
  open, onClose, onSubmit, title, submitLabel, canSystem, initial,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { name: string; description: string; category: SavedReportCategory; asSystemTemplate: boolean }) => void | Promise<void>;
  title: string;
  submitLabel: string;
  canSystem: boolean;
  initial: { name: string; description: string; category: SavedReportCategory; asSystemTemplate: boolean };
}) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [category, setCategory] = useState<SavedReportCategory>(initial.category);
  const [asSystem, setAsSystem] = useState(initial.asSystemTemplate);
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the dialog reopens
  useEffect(() => {
    if (open) {
      setName(initial.name);
      setDescription(initial.description);
      setCategory(initial.category);
      setAsSystem(initial.asSystemTemplate);
    }
  }, [open, initial.name, initial.description, initial.category, initial.asSystemTemplate]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Reports remember rows, measures, filters, and date range. Templates can be reused later from the Templates picker.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <Label htmlFor="report-name">Name</Label>
            <Input id="report-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Data performance by country" />
          </div>
          <div>
            <Label htmlFor="report-description">Description (optional)</Label>
            <Textarea id="report-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div>
            <Label htmlFor="report-category">Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as SavedReportCategory)}>
              <SelectTrigger id="report-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canSystem ? (
            <div className="flex items-center justify-between rounded border border-border px-3 py-2 bg-muted/30">
              <div>
                <div className="text-sm font-medium">Save as system template</div>
                <div className="text-xs text-muted-foreground">Visible to every tenant. Super admins only.</div>
              </div>
              <Switch checked={asSystem} onCheckedChange={setAsSystem} />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!name.trim() || submitting}
            onClick={async () => {
              setSubmitting(true);
              try {
                await onSubmit({ name: name.trim(), description: description.trim(), category, asSystemTemplate: asSystem });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? <Spinner className="mr-2 h-4 w-4" /> : null}{submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
